import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  Input,
  Button,
  Table,
  Select,
  InputNumber,
  Space,
  Card,
  Row,
  Col,
  AutoComplete,
  Checkbox,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { BillItem, CreateBillRequest } from "../../types/Billing";
import { StockItem } from "../../types/Stock";
import {
  fetchPatientsStart,
  createPatientSuccess,
} from "../../store/slices/billingSlice";
import { getStockItemsStart } from "../../store/slices/stockSlice";
import { formatStockAvail, getPackSize } from "../../utils/pack";
import adminApi from "../../services/adminApi";
import billingService from "../../services/billingService";
import { toast } from "../../utils/toast";

const { Option } = Select;

interface Doctor {
  _id: string;
  name: string;
  registrationNo?: string;
}

interface BillFormProps {
  onSubmit: (billData: CreateBillRequest) => void;
  loading?: boolean;
}

const emptyRow = (): BillItem => ({
  stockId: "",
  stockName: "",
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  tax: 0,
  total: 0,
});

const BillForm: React.FC<BillFormProps> = ({ onSubmit, loading = false }) => {
  const dispatch = useDispatch();
  const { patients } = useSelector((state: RootState) => state.billing);
  const { stockItems } = useSelector((state: RootState) => state.stock);

  const [form] = Form.useForm();
  const [items, setItems] = useState<BillItem[]>([emptyRow()]);
  const [patientId, setPatientId] = useState<string | undefined>();
  const [customerName, setCustomerName] = useState("");
  const [doctorId, setDoctorId] = useState<string | undefined>();
  const [doctorName, setDoctorName] = useState("");
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [savePatient, setSavePatient] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [taxMode, setTaxMode] = useState<"cgst_sgst" | "igst">("cgst_sgst");
  const [billDiscount, setBillDiscount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [roundOff, setRoundOff] = useState(0);
  const [gstTouched, setGstTouched] = useState(false);
  const [showPurchaseRate, setShowPurchaseRate] = useState(false);

  useEffect(() => {
    dispatch(fetchPatientsStart());
    dispatch(getStockItemsStart({ page: 1, limit: 500 }));
    adminApi
      .getDoctors()
      .then((res) => setDoctors(res.data.data || []))
      .catch(() => undefined);
    adminApi
      .getSettings()
      .then((res) => {
        if (res.data.data?.taxMode) setTaxMode(res.data.data.taxMode);
      })
      .catch(() => undefined);
  }, [dispatch]);

  const patientOptions = useMemo(() => {
    if (!Array.isArray(patients)) return [];
    const q = customerName.trim().toLowerCase();
    return patients
      .filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          p.phone?.includes(q) ||
          p.patientId?.toLowerCase().includes(q)
      )
      .slice(0, 20)
      .map((p) => ({
        value: p.name,
        label: `${p.name} · ${p.phone}${p.patientId ? ` · ${p.patientId}` : ""}`,
        patient: p,
      }));
  }, [patients, customerName]);

  const doctorOptions = useMemo(() => {
    const q = doctorName.trim().toLowerCase();
    return doctors
      .filter((d) => !q || d.name.toLowerCase().includes(q))
      .slice(0, 20)
      .map((d) => ({
        value: d.name,
        label: d.registrationNo ? `${d.name} (${d.registrationNo})` : d.name,
        doctor: d,
      }));
  }, [doctors, doctorName]);

  /** Qty already used on other bill lines for the same batch */
  const getCommittedQty = (stockId: string, exceptIndex?: number) =>
    items.reduce((sum, row, i) => {
      if (!stockId || row.stockId !== stockId || i === exceptIndex) return sum;
      return sum + (Number(row.quantity) || 0);
    }, 0);

  const getAvailableQty = (stockId: string, exceptIndex?: number) => {
    const stock = stockItems.find((s) => s._id === stockId);
    if (!stock) return 0;
    return Math.max(
      0,
      (stock.quantity?.current ?? 0) - getCommittedQty(stockId, exceptIndex)
    );
  };

  const isKnownPatient = Boolean(patientId);
  const showNewPatientFields = Boolean(customerName.trim()) && !isKnownPatient;

  const selectPatient = (p: any) => {
    setPatientId(p._id);
    setCustomerName(p.name);
    setSavePatient(false);
    form.setFieldsValue({
      phone: p.phone,
      age: p.age,
      gender: p.gender,
    });
  };

  const onCustomerNameChange = (value: string) => {
    setCustomerName(value);
    const match = Array.isArray(patients)
      ? patients.find((p) => p.name.toLowerCase() === value.trim().toLowerCase())
      : undefined;
    if (match) {
      selectPatient(match);
    } else {
      setPatientId(undefined);
    }
  };

  const handleSavePatientNow = async () => {
    try {
      const { phone, age, gender } = await form.validateFields([
        "phone",
        "age",
        "gender",
      ]);
      if (!customerName.trim()) {
        toast.error("Enter customer name");
        return;
      }
      setSavingPatient(true);
      const created = await billingService.createPatient({
        name: customerName.trim(),
        phone,
        age,
        gender,
      });
      dispatch(createPatientSuccess(created));
      selectPatient(created);
    } catch (e: any) {
      // API errors toasted by interceptor; form validation stays on fields
      if (!e?.response) {
        // validateFields rejection — ignore
      }
    } finally {
      setSavingPatient(false);
    }
  };

  const removeItem = (index: number) => {
    setItems((prev) => {
      let next = prev.filter((_, i) => i !== index);
      if (next.length === 0) next = [emptyRow()];
      else if (next[next.length - 1].stockId) next = [...next, emptyRow()];
      return next;
    });
  };

  const updateItem = (index: number, field: keyof BillItem, value: any) => {
    if (field === "stockId" && value) {
      const stock = stockItems.find((s) => s._id === value);
      const available = getAvailableQty(value, index);
      if (!stock || available <= 0) {
        toast.error(
          stock
            ? `"${stock.name}" (Batch ${stock.batchNumber}) has no stock left`
            : "Stock batch not found"
        );
        return;
      }
    }

    if (field === "quantity") {
      const row = items[index];
      if (row?.stockId) {
        const available = getAvailableQty(row.stockId, index);
        const qty = Number(value) || 0;
        if (qty > available) {
          toast.warning(
            `Only ${available} available for this batch (including other lines on this bill)`
          );
          value = available > 0 ? available : 1;
          if (available <= 0) return;
        }
        if (qty < 1) value = 1;
      }
    }

    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === "stockId") {
        const stock = stockItems.find((s) => s._id === value);
        if (stock) {
          const available = Math.max(
            0,
            (stock.quantity?.current ?? 0) -
              prev.reduce((sum, row, i) => {
                if (i === index || row.stockId !== value) return sum;
                return sum + (Number(row.quantity) || 0);
              }, 0)
          );
          next[index].stockName = stock.name;
          next[index].unitPrice = stock.pricing.sellingPrice;
          next[index].tax =
            (stock as any).gstRate ?? stock.pricing?.taxPercent ?? 12;
          next[index].quantity = Math.min(
            Math.max(1, next[index].quantity || 1),
            available
          );
        }
      }
      const item = next[index];
      const gross = item.quantity * item.unitPrice;
      const disc = (gross * item.discount) / 100;
      // Line amount is taxable only — GST is applied at bill level, not per line
      next[index].total = gross - disc;

      // Auto-add next empty row when medicine is picked on the last row
      if (field === "stockId" && value && index === next.length - 1) {
        next.push(emptyRow());
      }
      return next;
    });
  };

  const lineTotals = useMemo(() => {
    const gross = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
    const lineDiscount = items.reduce(
      (s, i) => s + (i.quantity * i.unitPrice * i.discount) / 100,
      0
    );
    const taxable = gross - lineDiscount;
    const computedGst = items.reduce((s, i) => {
      const g = i.quantity * i.unitPrice;
      const d = (g * i.discount) / 100;
      return s + ((g - d) * i.tax) / 100;
    }, 0);
    return {
      gross,
      lineDiscount,
      taxable: Number(taxable.toFixed(2)),
      computedGst: Number(computedGst.toFixed(2)),
    };
  }, [items]);

  useEffect(() => {
    if (!gstTouched) {
      setGstAmount(lineTotals.computedGst);
    }
  }, [lineTotals.computedGst, gstTouched]);

  const grandTotal = Number(
    (
      lineTotals.taxable +
      (gstAmount || 0) -
      (billDiscount || 0) +
      (roundOff || 0)
    ).toFixed(2)
  );

  const handleSubmit = async (values: any) => {
    const filledItems = items.filter((i) => i.stockId);
    if (filledItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      if (!row.stockId) continue;
      const available = getAvailableQty(row.stockId, i);
      const stock = stockItems.find((s) => s._id === row.stockId);
      if (available <= 0) {
        toast.error(
          `"${stock?.name || "Item"}" has no stock available — remove it from the bill`
        );
        return;
      }
      if (row.quantity > available) {
        toast.error(
          `Insufficient stock for ${stock?.name || "item"} (Batch ${
            stock?.batchNumber || ""
          }). Available: ${available}`
        );
        return;
      }
    }

    const name = customerName.trim() || "Walk-in Customer";
    let amountPaid = values.amountPaid;
    if (amountPaid == null || amountPaid === 0) {
      amountPaid = grandTotal;
    }

    onSubmit({
      patient: patientId,
      customer: patientId
        ? undefined
        : {
            name,
            phone: values.phone || "",
            age: values.age,
            gender: values.gender,
          },
      savePatient: !patientId && savePatient,
      doctor: doctorId,
      doctorName: doctorName.trim() || undefined,
      prescriptionNumber: values.prescriptionNumber || undefined,
      taxMode,
      billDiscount: billDiscount || 0,
      totalTax: gstAmount || 0,
      roundOff: roundOff || 0,
      items: filledItems.map((item) => ({
        medicine: item.stockId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
        gstRate: item.tax,
        total: item.quantity * item.unitPrice * (1 - item.discount / 100),
      })),
      subtotal: lineTotals.taxable,
      grandTotal,
      amountPaid,
      paymentMethod: values.paymentMethod || "cash",
      notes: values.notes,
    });
  };

  const handleReset = () => {
    form.resetFields();
    setItems([emptyRow()]);
    setPatientId(undefined);
    setCustomerName("");
    setDoctorId(undefined);
    setDoctorName("");
    setSavePatient(false);
    setBillDiscount(0);
    setGstAmount(0);
    setRoundOff(0);
    setGstTouched(false);
  };

  const columns = [
    {
      title: "Medicine / Batch",
      dataIndex: "stockId",
      key: "stockId",
      render: (value: string, _: BillItem, index: number) => {
        const options = stockItems.map((stock: StockItem) => {
          const remaining = getAvailableQty(stock._id, index);
          const med =
            typeof stock.medicine === "object" && stock.medicine
              ? stock.medicine
              : null;
          const availLabel = formatStockAvail(remaining, {
            packSize: med?.packSize,
            unit: stock.quantity.unit || med?.unit,
            purchaseUnit: med?.purchaseUnit,
          });
          const isCurrent = stock._id === value;
          const disabled = remaining <= 0 && !isCurrent;
          return {
            value: stock._id,
            disabled,
            label: disabled
              ? `${stock.name} | ${stock.batchNumber} | OUT OF STOCK`
              : `${stock.name} | ${stock.batchNumber} | ₹${stock.pricing.sellingPrice} | Left: ${availLabel}`,
          };
        });
        return (
          <Select
            size="small"
            showSearch
            style={{ width: "100%" }}
            options={options}
            placeholder="Search stock"
            value={value || undefined}
            onChange={(val) => updateItem(index, "stockId", val)}
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: (
        <span style={{ fontSize: 11, lineHeight: 1.2 }}>
          Pur. Rate
          <br />
          GST / Final
        </span>
      ),
      key: "purchaseRate",
      width: 118,
      align: "right" as const,
      render: (_: unknown, record: BillItem) => {
        if (!record.stockId) return "—";
        const stock = stockItems.find((s) => s._id === record.stockId);
        if (!stock) return "—";
        const med =
          typeof stock.medicine === "object" && stock.medicine
            ? stock.medicine
            : null;
        const packSize = getPackSize(med);
        // Stock stores per base unit; show Rate/pack as entered on purchase
        const ratePerPack = Number(
          ((stock.pricing?.purchasePrice || 0) * packSize).toFixed(2)
        );
        const gstPct =
          stock.gstRate ??
          med?.defaultGstRate ??
          stock.pricing?.taxPercent ??
          record.tax ??
          0;
        const rateAfterGst = Number(
          (ratePerPack * (1 + gstPct / 100)).toFixed(2)
        );
        return (
          <div
            title="Purchase rate / pack + GST (internal)"
            style={{
              color: "#666",
              fontSize: 11,
              lineHeight: 1.35,
              textAlign: "right",
            }}
          >
            <div>₹{ratePerPack.toFixed(2)}</div>
            <div>GST {gstPct}%</div>
            <div style={{ color: "#1677ff", fontWeight: 600 }}>
              ₹{rateAfterGst.toFixed(2)}
            </div>
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 150,
      render: (value: number, record: BillItem, index: number) => {
        const stock = stockItems.find((s) => s._id === record.stockId);
        const med =
          stock && typeof stock.medicine === "object" ? stock.medicine : null;
        const packSize = getPackSize(med);
        const maxQty = record.stockId
          ? getAvailableQty(record.stockId, index)
          : undefined;
        return (
          <Space size={2}>
            <InputNumber
              controls={false}
              size="small"
              min={1}
              max={maxQty}
              value={value}
              disabled={!record.stockId}
              onChange={(val) => updateItem(index, "quantity", val || 1)}
              style={{ width: 70 }}
            />
            {packSize > 1 && record.stockId && (
              <Button
                size="small"
                type="link"
                style={{ padding: 0 }}
                disabled={!maxQty || (value || 0) + packSize > maxQty}
                onClick={() =>
                  updateItem(index, "quantity", (value || 0) + packSize)
                }
              >
                +strip
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "Rate",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 90,
      render: (value: number, _: BillItem, index: number) => (
        <InputNumber
          controls={false}
          size="small"
          min={0}
          precision={2}
          value={value}
          onChange={(val) => updateItem(index, "unitPrice", val || 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Disc%",
      dataIndex: "discount",
      key: "discount",
      width: 70,
      render: (value: number, _: BillItem, index: number) => (
        <InputNumber
          controls={false}
          size="small"
          min={0}
          max={100}
          value={value}
          onChange={(val) => updateItem(index, "discount", val || 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Amt",
      dataIndex: "total",
      key: "total",
      width: 80,
      align: "right" as const,
      render: (value: number) => `₹${(value || 0).toFixed(2)}`,
    },
    {
      title: "",
      key: "action",
      width: 36,
      render: (_: any, record: BillItem, index: number) =>
        record.stockId ? (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(index)}
          />
        ) : null,
    },
  ];

  const visibleColumns = showPurchaseRate
    ? columns
    : columns.filter((col) => col.key !== "purchaseRate");

  return (
    <div className="bill-create-page">
      <Card
        size="small"
        title="New Bill"
        styles={{
          body: { padding: 12, paddingBottom: 0 },
          header: { minHeight: 40, padding: "0 12px" },
        }}
        style={{ borderBottom: "none", borderRadius: "8px 8px 0 0" }}
      >
        <Form
          form={form}
          layout="vertical"
          size="middle"
          onFinish={handleSubmit}
          requiredMark={false}
          initialValues={{ paymentMethod: "cash" }}
          style={{ marginBottom: 0 }}
          className="bill-create-form"
        >
          <Row gutter={[8, 0]}>
            <Col xs={24} md={10}>
              <Form.Item label="Customer" style={{ marginBottom: 8 }}>
                <AutoComplete
                  value={customerName}
                  options={patientOptions}
                  onChange={onCustomerNameChange}
                  onSelect={(_v, option: any) => {
                    if (option?.patient) selectPatient(option.patient);
                  }}
                  placeholder="Type name or phone — pick patient or keep as walk-in"
                  allowClear
                  onClear={() => {
                    setCustomerName("");
                    setPatientId(undefined);
                    form.setFieldsValue({
                      phone: undefined,
                      age: undefined,
                      gender: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item
                name="phone"
                label="Phone"
                style={{ marginBottom: 8 }}
                rules={
                  showNewPatientFields && savePatient
                    ? [{ required: true, message: "Required to save" }]
                    : []
                }
              >
                <Input placeholder="Phone" disabled={isKnownPatient} />
              </Form.Item>
            </Col>
            <Col xs={6} md={3}>
              <Form.Item
                name="age"
                label="Age"
                style={{ marginBottom: 8 }}
                rules={
                  showNewPatientFields && savePatient
                    ? [{ required: true, message: "Required" }]
                    : []
                }
              >
                <InputNumber
                  controls={false}
                  min={0}
                  max={150}
                  style={{ width: "100%" }}
                  disabled={isKnownPatient}
                />
              </Form.Item>
            </Col>
            <Col xs={6} md={3}>
              <Form.Item
                name="gender"
                label="Gender"
                style={{ marginBottom: 8 }}
                rules={
                  showNewPatientFields && savePatient
                    ? [{ required: true, message: "Required" }]
                    : []
                }
              >
                <Select allowClear disabled={isKnownPatient} placeholder="-">
                  <Option value="male">M</Option>
                  <Option value="female">F</Option>
                  <Option value="other">O</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item label=" " style={{ marginBottom: 8 }}>
                {showNewPatientFields ? (
                  <Space size={4} wrap>
                    <Checkbox
                      checked={savePatient}
                      onChange={(e) => setSavePatient(e.target.checked)}
                    >
                      Save
                    </Checkbox>
                    <Button
                      size="small"
                      type="link"
                      loading={savingPatient}
                      onClick={handleSavePatientNow}
                      style={{ padding: 0 }}
                    >
                      Add now
                    </Button>
                  </Space>
                ) : isKnownPatient ? (
                  <span style={{ color: "#52c41a", fontSize: 12 }}>
                    Registered
                  </span>
                ) : (
                  <span style={{ color: "#999", fontSize: 12 }}>Optional</span>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[8, 0]}>
            <Col xs={24} md={8}>
              <Form.Item label="Doctor" style={{ marginBottom: 8 }}>
                <AutoComplete
                  value={doctorName}
                  options={doctorOptions}
                  onChange={(v) => {
                    setDoctorName(v);
                    const match = doctors.find(
                      (d) => d.name.toLowerCase() === v.trim().toLowerCase()
                    );
                    setDoctorId(match?._id);
                  }}
                  onSelect={(_v, option: any) => {
                    if (option?.doctor) {
                      setDoctorId(option.doctor._id);
                      setDoctorName(option.doctor.name);
                    }
                  }}
                  placeholder="Doctor name (optional)"
                  allowClear
                  onClear={() => {
                    setDoctorName("");
                    setDoctorId(undefined);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="prescriptionNumber"
                label="Rx No."
                style={{ marginBottom: 8 }}
              >
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="paymentMethod"
                label="Pay"
                style={{ marginBottom: 8 }}
              >
                <Select className="bill-tall-control">
                  <Option value="cash">Cash</Option>
                  <Option value="upi">UPI</Option>
                  <Option value="card">Card</Option>
                  <Option value="net_banking">NetBank</Option>
                  <Option value="insurance">Insurance</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="amountPaid"
                label="Paid ₹"
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  controls={false}
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  className="bill-tall-control"
                  placeholder={grandTotal.toFixed(2)}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="notes" label="Note" style={{ marginBottom: 8 }}>
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <Button
              type="link"
              size="small"
              htmlType="button"
              style={{ padding: 0, height: "auto", fontSize: 12 }}
              onClick={() => setShowPurchaseRate((v) => !v)}
            >
              {showPurchaseRate ? "Hide Pur. Rate" : "Show Pur. Rate"}
            </Button>
          </div>

          <Table
            size="small"
            columns={visibleColumns}
            dataSource={items.map((item, index) => ({ ...item, key: index }))}
            pagination={false}
            scroll={{ x: 700, y: "calc(100vh - 360px)" }}
            style={{ marginBottom: 0 }}
            locale={{ emptyText: "Select a medicine to start" }}
          />
        </Form>
      </Card>

      <div className="bill-create-footer">
        <Space size={10} wrap style={{ flex: 1 }}>
          <span style={{ fontSize: 13, color: "#666" }}>
            Taxable ₹{lineTotals.taxable.toFixed(2)}
            {lineTotals.lineDiscount > 0
              ? ` (line disc ₹${lineTotals.lineDiscount.toFixed(2)})`
              : ""}
          </span>
          <span>
            GST ₹{" "}
            <InputNumber
              controls={false}
              size="middle"
              min={0}
              precision={2}
              value={gstAmount}
              onChange={(v) => {
                setGstTouched(true);
                setGstAmount(v || 0);
              }}
              style={{ width: 100 }}
            />
          </span>
          <span>
            Bill Disc ₹{" "}
            <InputNumber
              controls={false}
              size="middle"
              min={0}
              precision={2}
              value={billDiscount}
              onChange={(v) => setBillDiscount(v || 0)}
              style={{ width: 100 }}
            />
          </span>
          <span>
            Round ₹{" "}
            <InputNumber
              controls={false}
              size="middle"
              precision={2}
              step={0.01}
              value={roundOff}
              onChange={(v) => setRoundOff(v || 0)}
              style={{ width: 90 }}
            />
          </span>
          <strong style={{ fontSize: 16 }}>Total ₹{grandTotal.toFixed(2)}</strong>
        </Space>
        <Space>
          <Button onClick={handleReset}>Reset</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Create Bill
          </Button>
        </Space>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .bill-create-page {
            display: flex;
            flex-direction: column;
            min-height: calc(100vh - 100px);
          }
          .bill-create-page > .ant-card {
            flex: 1;
          }
          .bill-create-footer {
            position: sticky;
            bottom: 0;
            z-index: 30;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px 16px;
            background: #fff;
            border: 1px solid #f0f0f0;
            border-top: 1px solid #e8e8e8;
            border-radius: 0 0 8px 8px;
            box-shadow: 0 -4px 12px rgba(0,0,0,0.06);
          }
          .bill-create-form .ant-input,
          .bill-create-form .ant-input-affix-wrapper,
          .bill-create-form .ant-select-selector,
          .bill-create-form .ant-input-number,
          .bill-create-form .ant-input-number-input {
            min-height: 40px !important;
          }
          .bill-create-form .ant-select-single .ant-select-selector {
            display: flex;
            align-items: center;
          }
          .bill-create-form .ant-input-number-input {
            height: 38px !important;
          }
          .bill-create-form .bill-tall-control.ant-select .ant-select-selector,
          .bill-create-form .bill-tall-control.ant-input-number {
            min-height: 44px !important;
            height: 44px !important;
          }
          .bill-create-form .bill-tall-control.ant-input-number .ant-input-number-input {
            height: 42px !important;
          }
        `,
        }}
      />
    </div>
  );
};

export default BillForm;
